import { FC, PropsWithChildren, useEffect } from 'react';
import { useNavigate } from 'react-router';

import {
  selectCurrentToken,
  selectCurrentUser,
} from '@src/store/auth/auth-selectors';
import { logOut, setCredentials } from '@src/store/auth/auth-slice';
import { useAppDispatch, useAppSelector } from '@src/store/store';

import paths from '@src/routes/routes';
import {
  decodeToken,
  getSessionToken,
  getToken,
  isTokenValid,
} from '@src/utils/auth';

type AuthGuardProps = PropsWithChildren;

const AuthGuard: FC<AuthGuardProps> = (props) => {
  const { children } = props;
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectCurrentUser);
  const token =
    useAppSelector(selectCurrentToken) ?? getToken() ?? getSessionToken();
  const isValid = isTokenValid(token);

  const navigate = useNavigate();

  const rememberMe = localStorage.getItem('rememberMe');
  const shouldRemember = JSON.parse(rememberMe ?? 'false') === true;

  useEffect(() => {
    if (token && isValid) {
      const data = decodeToken(token);
      dispatch(
        setCredentials({
          user: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            role: data.role,
          },
          token,
          rememberMe: shouldRemember,
        })
      );
    } else {
      dispatch(logOut());
      navigate(paths.signIn, { replace: true });
    }
  }, [token, isValid, shouldRemember, dispatch, navigate]);

  if (!isValid || !user) {
    return null;
  }

  return children;
};

export default AuthGuard;
